import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
  DialogProps,
  InputAdornment,
} from "@mui/material";
import { Close, Send, ChatBubbleOutline } from "@mui/icons-material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { createComment, getComments } from "apis";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { ProfileState } from "store/features/profile";
import dayjs from "dayjs";
import { invalidateEvents } from "store/features/events";
import { stringAvatar } from "utils";

interface CommentDialogProps extends DialogProps {
  onClose: () => void;
  event: { id: string };
}

const CommentDialog: React.FC<CommentDialogProps> = ({
  open,
  onClose,
  event,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, handleSubmit, reset } = useForm<{ comment: string }>();
  const dispatch = useDispatch();
  const { item: profile } = useSelector<RootState, ProfileState>(
    (store) => store.profile,
  );

  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dirty, setDirty] = useState(false);

  const eventId = event.id;

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    getComments(eventId)
      .then((data) => setComments(data || []))
      .finally(() => setLoading(false));
  }, [eventId]);

  const sendComment: SubmitHandler<{ comment: string }> = (data) => {
    if (!data.comment.trim()) return;
    setSending(true);
    createComment(Number(eventId), { content: data.comment })
      .then((newComment) => {
        newComment.createdAt = new Date().toISOString();
        if (!newComment.poster?.firstName && profile) {
          newComment.poster = {
            firstName: profile.firstName,
            lastName: profile.lastName,
            photo: profile.photo,
          };
        }
        setComments((prev) => [...prev, newComment]);
        setDirty(true);
        reset();
      })
      .finally(() => setSending(false));
  };

  const handleClose = () => {
    if (dirty) dispatch(invalidateEvents());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-end",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "400px", md: "420px" },
          maxWidth: "100%",
          height: "100%",
          maxHeight: "100%",
          m: 0,
          borderRadius: { xs: 0, sm: "12px 0 0 12px" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
        >
          <Close fontSize="small" />
        </IconButton>
        <ChatBubbleOutline sx={{ color: "primary.main", fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          {t("comments")}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
          {comments.length}
        </Typography>
      </Box>

      {/* Comments list */}
      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <CircularProgress size={28} />
          </Box>
        ) : comments.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            color="text.disabled"
          >
            <ChatBubbleOutline
              sx={{ fontSize: 48, mb: 1, color: "action.disabled" }}
            />
            <Typography variant="body2">{t("comments")} — 0</Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5} mt="auto">
            {comments.map(
              ({ content, poster, createdAt }: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2.5,
                    px: 2,
                    py: 1.5,
                    maxWidth: "85%",
                    alignSelf: "flex-end",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.5,
                      mb: 1,
                      direction: "auto",
                      unicodeBidi: "plaintext",
                    }}
                  >
                    {content}
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.disabled">
                      {dayjs(createdAt).format("DD MMM · HH:mm")}
                    </Typography>
                    {poster?.firstName && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Avatar
                          src={poster?.photo as string | undefined}
                          {...(!poster?.photo
                            ? stringAvatar(
                                `${poster.firstName} ${poster.lastName}`,
                                { width: 20, height: 20, fontSize: 9 },
                              )
                            : { sx: { width: 20, height: 20 } })}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          {poster.firstName} {poster.lastName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ),
            )}
          </Box>
        )}
      </DialogContent>

      {/* Input */}
      <Box
        component="form"
        onSubmit={handleSubmit(sendComment)}
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={t("inputs.comment") + "..."}
          {...register("comment")}
          InputProps={{
            sx: {
              borderRadius: 3,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="submit"
                  size="small"
                  color="primary"
                  disabled={sending}
                >
                  {sending ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Send sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Dialog>
  );
};

export default CommentDialog;
